// web/src/components/ts-components/ProcessingModal/ProcessingModal.tsx
import * as React from 'react'
import styled from '@emotion/styled'
import { Progress } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { zIndex } from '../../css'
import Text from '../Text'
import ReprocessingIcon from '../icons/ReprocessingIcon'

interface ProcessingModalProps {
  open: boolean
  /** default: 'Processing The Request' */
  title?: string
  /** default: the "safely leave this page" copy */
  description?: string
  /** 0–100, controlled by the caller */
  progress: number
  /** default: tenant-primary ReprocessingIcon */
  icon?: React.ReactNode
}

const DEFAULT_TITLE = 'Processing The Request'
const DEFAULT_DESCRIPTION =
  'You can safely leave this page open. You will be redirected once processing is complete.'

/**
 * Non-dismissable, presentational progress modal. open + progress are owned by
 * the caller, so it is reusable for any long-running async action. Mirrors the
 * Reprocessing Statistics overlay's look. Note: relies on zIndex.base and is
 * intended to render within a high-stacking-context portal (like the wizard
 * modal); if reused standalone at the app root, the caller may need a higher
 * z-index.
 */
const ProcessingModal: React.FC<ProcessingModalProps> = ({
  open,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  progress,
  icon,
}) => {
  if (!open) {
    return null
  }

  const rounded = Math.round(progress)

  return (
    <Overlay>
      <Card>
        {icon ?? (
          <ReprocessingIcon
            size={56}
            backgroundColor={colors.PRIMARY}
            iconColor={colors.WHITE}
          />
        )}

        <Text
          variant='heading3'
          weight='semiBold'
          color={colors.WHITE}
          style={'text-align: center;'}
        >
          {title}
        </Text>

        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.WHITE}
          style={'text-align: center;'}
        >
          {description}
        </Text>

        <ProgressWrapper>
          <Progress
            percent={rounded}
            showInfo={false}
            strokeColor={colors.PRIMARY}
            trailColor={'rgba(120, 120, 120, 0.2)'}
            strokeWidth={24}
          />
          <PercentLabel>{rounded}%</PercentLabel>
        </ProgressWrapper>
      </Card>
    </Overlay>
  )
}

export default ProcessingModal

// ---- styled ----
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${zIndex.base};
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
`

const Card = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 500px;
  padding: 24px;
  background-color: #1b222b;
  border: 1px solid #27303e;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
`

const ProgressWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 8px;
`

const PercentLabel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.WHITE};
  pointer-events: none;
`
